import {
  type DragEvent as ReactDragEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { Button, Typography } from "Atoms";
import {
  Add,
  Close,
  DeleteOutline,
  DragIndicator,
  EditOutlined,
  InfoOutlined,
  LockOutlined,
  PlaylistAdd,
  RemoveCircleOutline,
  Search,
  Close as ClearIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { getFlatRequirementsBySourcePage } from "Utils/helperFunctions";
import { useI18n } from "Utils/i18n";
import { updateTestPlanAPI } from "Zustand/services/projectsService";
import { useBoundStore } from "Zustand/store/mainStore";
import TestRequirementForm from "./TesRequirementContentForm";

type RequirementItem = {
  nodeid: string;
  // Identity of a card in the flow. The same requirement (nodeid) can appear
  // several times in a leg, and each card keeps its own settings.
  instanceId?: string;
  name?: string;
  category?: string;
  testrequirementdata?: Record<string, any>;
  [key: string]: any;
};

type RequirementSlot = RequirementItem | null;

type Branch = {
  id: string;
  name: string;
  requirements: RequirementSlot[];
};

type SampleBreakout = {
  id: string;
  name: string;
  quantity: string;
  branchQuantities: Record<string, string>;
};

type Leg = {
  id: string;
  name: string;
  dutCount: string;
  branches: Branch[];
  sampleBreakouts: SampleBreakout[];
};

type DragPayload =
  | {
      source: "pool";
      requirementId: string;
    }
  | {
      source: "leg";
      requirementId: string;
      legId: string;
      branchId: string;
      index: number;
    };

const createBranch = (index: number): Branch => ({
  id: `branch-${Date.now()}-${index}`,
  name: String(index + 1),
  requirements: [],
});

const createSampleBreakout = (index: number): SampleBreakout => ({
  id: `sample-breakout-${Date.now()}-${index}`,
  name: "",
  quantity: "",
  branchQuantities: {},
});

const SAMPLE_BREAKOUTS_TAB = "sample-breakouts";

const createLeg = (index: number): Leg => ({
  id: `leg-${Date.now()}-${index}`,
  name: String.fromCharCode(65 + index),
  dutCount: "",
  branches: [createBranch(0)],
  sampleBreakouts: [],
});

const createRequirementInstance = (
  requirement: RequirementItem
): RequirementItem => ({
  ...requirement,
  instanceId: uuidv4(),
});

const getRequirementData = (requirement: RequirementItem) =>
  requirement.testrequirementdata &&
  typeof requirement.testrequirementdata === "object" &&
  !Array.isArray(requirement.testrequirementdata)
    ? requirement.testrequirementdata
    : requirement;

const getRequirementName = (requirement: RequirementItem) =>
  getRequirementData(requirement)?.name || requirement.name || "";

const getRequirementCategory = (requirement: RequirementItem) =>
  getRequirementData(requirement)?.category ||
  getRequirementData(requirement)?.subcategory ||
  requirement.category ||
  "";

const getRequirementIncludeInTestPlan = (requirement: RequirementItem) =>
  getRequirementData(requirement)?.includeintestplan ??
  requirement.includeintestplan ??
  false;

const highlightSearchMatch = (text: string, query: string) => {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return text;

  const matchIndex = text.toLowerCase().indexOf(trimmedQuery.toLowerCase());
  if (matchIndex < 0) return text;

  return (
    <>
      {text.slice(0, matchIndex)}
      <Box
        component="span"
        sx={{
          bgcolor: "#fff59d",
          color: "inherit",
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {text.slice(matchIndex, matchIndex + trimmedQuery.length)}
      </Box>
      {text.slice(matchIndex + trimmedQuery.length)}
    </>
  );
};

const buildTestRequirementData = (requirement: RequirementItem) => ({
  ...getRequirementData(requirement),
  nodeid: requirement.nodeid,
  name: getRequirementName(requirement),
  category: getRequirementCategory(requirement),
});

const normalizeRequirementRows = (requirements: RequirementSlot[]) =>
  Array.from(
    { length: requirements.length },
    (_, index) => requirements[index] ?? null
  );

const trimTrailingEmptyRequirementRows = (branches: Branch[]) => {
  const lastPopulatedRowIndex = Math.max(
    -1,
    ...branches.flatMap((branch) =>
      normalizeRequirementRows(branch.requirements).map((requirement, index) =>
        requirement ? index : -1
      )
    )
  );

  return branches.map((branch) => ({
    ...branch,
    requirements: normalizeRequirementRows(branch.requirements).slice(
      0,
      lastPopulatedRowIndex + 1
    ),
  }));
};

const buildLegsPayload = (legs: Leg[]) =>
  legs.map((leg, legIndex) => ({
    id: leg.id,
    name: leg.name,
    order: legIndex + 1,
    samplebreakouts: leg.sampleBreakouts.map((breakout) => ({
      name: breakout.name,
      quantity: Number(breakout.quantity),
      branchQuantities: Object.fromEntries(
        Object.entries(breakout.branchQuantities).map(([branchId, quantity]) => [
          branchId,
          Number(quantity || 0),
        ])
      ),
    })),
    branches: leg.branches.map((branch, branchIndex) => ({
      id: branch.id,
      branch: String(branchIndex + 1),
      requirements: branch.requirements
        .flatMap((requirement, requirementIndex) =>
          requirement
            ? [
                {
                  nodeid: requirement.nodeid,
                  instanceid: requirement.instanceId,
                  name: getRequirementName(requirement),
                  category: getRequirementCategory(requirement),
                  order: requirementIndex + 1,
                  row: requirementIndex + 1,
                  testrequirementdata: buildTestRequirementData(requirement),
                },
              ]
            : []
        ),
    })),
  }));

const hydrateLegsFromPlan = (planData: any): Leg[] => {
  const backendLegs = planData?.testplan?.legs ?? planData?.legs ?? [];
  if (!Array.isArray(backendLegs)) return [];

  const hydrateRequirements = (requirements: any[] = []) => {
    const hydratedRequirements = requirements.map((requirement: any) => ({
      nodeid: requirement.nodeid,
      // Plans saved before repeats were supported have no instanceid yet.
      instanceId: requirement.instanceid ?? uuidv4(),
      name: requirement.name,
      category: requirement.category ?? "",
      order: requirement.order,
      row: requirement.row,
      testrequirementdata: requirement.testrequirementdata,
    }));
    const usesZeroBasedRows = hydratedRequirements.some(
      (requirement) => requirement.row === 0
    );

    // Branch payloads omit empty matrix cells, so row/order metadata is needed
    // to rebuild the sparse layout after a save-and-refetch cycle. Backend row
    // numbering is 1-based, while React arrays stay 0-based internally.
    return hydratedRequirements.reduce<RequirementSlot[]>(
      (acc, requirement, fallbackIndex) => {
        const rowIndex =
          typeof requirement.row === "number"
            ? requirement.row - (usesZeroBasedRows ? 0 : 1)
            : typeof requirement.order === "number"
              ? requirement.order - 1
              : fallbackIndex;

        acc[Math.max(0, rowIndex)] = requirement;
        return acc;
      },
      []
    );
  };

  return backendLegs.map((leg: any, index: number) => {
    const legacyRequirements = Array.isArray(leg.requirements)
      ? hydrateRequirements(leg.requirements)
      : [];
    const backendBranches = Array.isArray(leg.branches) ? leg.branches : [];
    const branches =
      backendBranches.length > 0
        ? backendBranches.map((branch: any, branchIndex: number) => ({
            id: branch.id ?? `branch-${Date.now()}-${index}-${branchIndex}`,
            name: String(branch.branch ?? branch.name ?? branchIndex + 1),
            requirements: hydrateRequirements(branch.requirements),
          }))
        : [
            {
              id: `branch-${Date.now()}-${index}-0`,
              name: "1",
              requirements: legacyRequirements,
            },
          ];
    const sampleBreakoutsSource = Array.isArray(leg.samplebreakouts)
      ? leg.samplebreakouts
      : Array.isArray(leg.sampleBreakouts)
        ? leg.sampleBreakouts
        : [];

    return {
      id: leg.id ?? `leg-${Date.now()}-${index}`,
      name: leg.name ?? String.fromCharCode(65 + index),
      dutCount:
        leg.dutCount !== undefined && leg.dutCount !== null
          ? String(leg.dutCount)
          : "",
      branches,
      sampleBreakouts: sampleBreakoutsSource.map(
        (breakout: any, breakoutIndex: number) => ({
          id:
            breakout.id ??
            `sample-breakout-${Date.now()}-${index}-${breakoutIndex}`,
          name: breakout.name ?? "",
          quantity:
            breakout.quantity !== undefined && breakout.quantity !== null
              ? String(breakout.quantity)
              : "",
          branchQuantities: Object.entries(
            breakout.branchQuantities ?? {}
          ).reduce<Record<string, string>>((acc, [branchId, quantity]) => {
            acc[branchId] =
              quantity !== undefined && quantity !== null
                ? String(quantity)
                : "";
            return acc;
          }, {}),
        })
      ),
    };
  });
};

export default function TestFlow({
  testPlanNodeId,
}: {
  testPlanNodeId: string;
}) {
  const { selectedTestPlanContent, getTestPlanContent } = useBoundStore();
  const { t } = useI18n();

  const requirements = useMemo<RequirementItem[]>(() => {
    if (!selectedTestPlanContent) return [];

    // Test Flow uses the same direct requirement list as the viewer, sorted by
    // source-reference page, while preserving drag/drop assignment behavior.
    // Test Flow should only offer active requirements, which the backend marks
    // with includeintestplan. Inactive requirements stay editable elsewhere.
    return getFlatRequirementsBySourcePage(selectedTestPlanContent)
      .filter((req: any) => getRequirementIncludeInTestPlan(req))
      .map((req: any) => ({
        ...req,
        nodeid: req.nodeid,
        name: req.name || t("testFlow.unnamedRequirement"),
        category: req.category ?? req.subcategory ?? "",
        testrequirementdata: req.testrequirementdata ?? {
          ...req,
          category: req.category ?? req.subcategory ?? "",
        },
      }));
  }, [selectedTestPlanContent, t]);

  const [legs, setLegs] = useState<Leg[]>([]);
  const [requirementSearch, setRequirementSearch] = useState("");
  const [savedLegsSnapshot, setSavedLegsSnapshot] = useState<string>("[]");
  const [activeLegId, setActiveLegId] = useState<string>("");
  const [activeBranchIdsByLeg, setActiveBranchIdsByLeg] = useState<
    Record<string, string>
  >({});
  const [editingLegId, setEditingLegId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<DragPayload | null>(null);
  const [dragOverLegId, setDragOverLegId] = useState<string | null>(null);
  const [dragOverRequirementId, setDragOverRequirementId] = useState<string | null>(
    null
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isRequirementEditorOpen, setIsRequirementEditorOpen] = useState(false);
  const [editingRequirement, setEditingRequirement] =
    useState<RequirementItem | null>(null);
  const branchMatrixScrollRef = useRef<HTMLDivElement | null>(null);
  const branchMatrixAutoScrollFrameRef = useRef<number | null>(null);
  const viewportAutoScrollFrameRef = useRef<number | null>(null);

  const stopBranchMatrixAutoScroll = useCallback(() => {
    if (branchMatrixAutoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(branchMatrixAutoScrollFrameRef.current);
      branchMatrixAutoScrollFrameRef.current = null;
    }
  }, []);

  const stopViewportAutoScroll = useCallback(() => {
    if (viewportAutoScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(viewportAutoScrollFrameRef.current);
      viewportAutoScrollFrameRef.current = null;
    }
  }, []);

  const startViewportAutoScroll = useCallback(
    (clientY: number) => {
      const edgeThreshold = 120;
      const maxScrollStep = 22;
      const viewportHeight = window.innerHeight;
      const topIntensity =
        clientY < edgeThreshold ? (edgeThreshold - clientY) / edgeThreshold : 0;
      const bottomIntensity =
        viewportHeight - clientY < edgeThreshold
          ? (edgeThreshold - (viewportHeight - clientY)) / edgeThreshold
          : 0;
      const scrollDirection =
        bottomIntensity > 0
          ? bottomIntensity
          : topIntensity > 0
            ? -topIntensity
            : 0;

      stopViewportAutoScroll();

      if (scrollDirection === 0) return;

      // Vertical auto-scroll covers long requirement/matrix workflows where the
      // drop target is outside the current viewport while the user is dragging.
      const scroll = () => {
        window.scrollBy(0, scrollDirection * maxScrollStep);
        viewportAutoScrollFrameRef.current =
          window.requestAnimationFrame(scroll);
      };

      viewportAutoScrollFrameRef.current =
        window.requestAnimationFrame(scroll);
    },
    [stopViewportAutoScroll]
  );

  const handleBranchMatrixAutoScroll = (event: ReactDragEvent<HTMLElement>) => {
    const container = branchMatrixScrollRef.current;

    if (!container || !dragging) {
      stopBranchMatrixAutoScroll();
      return;
    }

    const rect = container.getBoundingClientRect();
    const edgeThreshold = 96;
    const maxScrollStep = 18;
    const leftDistance = event.clientX - rect.left;
    const rightDistance = rect.right - event.clientX;
    const leftIntensity =
      leftDistance < edgeThreshold
        ? (edgeThreshold - leftDistance) / edgeThreshold
        : 0;
    const rightIntensity =
      rightDistance < edgeThreshold
        ? (edgeThreshold - rightDistance) / edgeThreshold
        : 0;
    const scrollDirection =
      rightIntensity > 0
        ? rightIntensity
        : leftIntensity > 0
          ? -leftIntensity
          : 0;

    stopBranchMatrixAutoScroll();

    if (scrollDirection === 0) return;

    // Auto-scroll lets users reach off-screen split branches while dragging
    // without needing to manually grab the horizontal scrollbar mid-drop.
    const scroll = () => {
      container.scrollLeft += scrollDirection * maxScrollStep;
      branchMatrixAutoScrollFrameRef.current =
        window.requestAnimationFrame(scroll);
    };

    branchMatrixAutoScrollFrameRef.current =
      window.requestAnimationFrame(scroll);
  };

  useEffect(() => {
    const hydratedLegs = hydrateLegsFromPlan(selectedTestPlanContent);
    setLegs(hydratedLegs);
    setSavedLegsSnapshot(JSON.stringify(buildLegsPayload(hydratedLegs)));
    setActiveBranchIdsByLeg(
      hydratedLegs.reduce<Record<string, string>>((acc, leg) => {
        acc[leg.id] = leg.branches[0]?.id ?? "";
        return acc;
      }, {})
    );
  }, [selectedTestPlanContent]);

  useEffect(() => {
    return () => {
      stopBranchMatrixAutoScroll();
      stopViewportAutoScroll();
    };
  }, [stopBranchMatrixAutoScroll, stopViewportAutoScroll]);

  useEffect(() => {
    if (!dragging) {
      stopViewportAutoScroll();
      return;
    }

    const handleWindowDragOver = (event: globalThis.DragEvent) => {
      startViewportAutoScroll(event.clientY);
    };
    const handleWindowDragEnd = () => {
      stopViewportAutoScroll();
    };

    window.addEventListener("dragover", handleWindowDragOver);
    window.addEventListener("drop", handleWindowDragEnd);
    window.addEventListener("dragend", handleWindowDragEnd);

    return () => {
      window.removeEventListener("dragover", handleWindowDragOver);
      window.removeEventListener("drop", handleWindowDragEnd);
      window.removeEventListener("dragend", handleWindowDragEnd);
      stopViewportAutoScroll();
    };
  }, [dragging, startViewportAutoScroll, stopViewportAutoScroll]);

  useEffect(() => {
    if (legs.length === 0) {
      if (activeLegId !== "") {
        setActiveLegId("");
      }
      return;
    }

    if (!legs.some((leg) => leg.id === activeLegId) && legs[0]) {
      setActiveLegId(legs[0].id);
    }
  }, [activeLegId, legs]);

  const activeLeg = legs.find((leg) => leg.id === activeLegId) || null;
  const safeActiveLegId = legs.some((leg) => leg.id === activeLegId)
    ? activeLegId
    : false;
  const activeBranchId = activeLeg
    ? activeBranchIdsByLeg[activeLeg.id] || activeLeg.branches[0]?.id || ""
    : "";
  const activeBranch =
    activeBranchId === SAMPLE_BREAKOUTS_TAB
      ? null
      : activeLeg?.branches.find((branch) => branch.id === activeBranchId) ||
        activeLeg?.branches[0] ||
        null;
  const assignedRequirementIds = useMemo(() => {
    const ids = new Set<string>();
    legs.forEach((leg) => {
      leg.branches.forEach((branch) => {
        branch.requirements.forEach((requirement) => {
          if (requirement) ids.add(requirement.nodeid);
        });
      });
    });
    return ids;
  }, [legs]);

  const assignedLegNamesByRequirement = useMemo(() => {
    const map = new Map<string, string[]>();
    legs.forEach((leg) => {
      leg.branches.forEach((branch) => {
        branch.requirements.forEach((requirement) => {
          if (!requirement) return;
          const current = map.get(requirement.nodeid) ?? [];
          const legName = `${leg.name} / ${t("testFlow.branchLabel", {
            branchName: branch.name,
          })}`;
          // A repeated test lists its leg/branch only once.
          if (current.includes(legName)) return;
          map.set(requirement.nodeid, [...current, legName]);
        });
      });
    });
    return map;
  }, [legs, t]);

  const allRequirements = useMemo(
    () => requirements,
    [requirements]
  );

  const filteredRequirements = useMemo(() => {
    const query = requirementSearch.trim().toLowerCase();
    if (!query) return requirements;

    return requirements.filter((requirement) => {
      const searchableText = [
        getRequirementName(requirement),
        getRequirementCategory(requirement),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [requirements, requirementSearch]);

  const handleAddLeg = () => {
    const nextLeg = createLeg(legs.length);
    setLegs((current) => [...current, nextLeg]);
    setActiveLegId(nextLeg.id);
    setActiveBranchIdsByLeg((current) => ({
      ...current,
      [nextLeg.id]: nextLeg.branches[0]?.id ?? "",
    }));
    setEditingLegId(nextLeg.id);
  };

  const handleDeleteLeg = () => {
    if (!activeLeg) return;
    setLegs((current) => current.filter((leg) => leg.id !== activeLeg.id));
    setActiveBranchIdsByLeg((current) => {
      const next = { ...current };
      delete next[activeLeg.id];
      return next;
    });
    toast.success(
      t("testFlow.deleteLegLocalSuccess", { legName: activeLeg.name })
    );
  };

  const updateLeg = (legId: string, updater: (leg: Leg) => Leg) => {
    setLegs((current) => current.map((leg) => (leg.id === legId ? updater(leg) : leg)));
  };

  const handleAddBranch = (legId: string) => {
    const targetLeg = legs.find((leg) => leg.id === legId);
    if (!targetLeg) return;

    const nextBranch = createBranch(targetLeg.branches.length);
    updateLeg(legId, (leg) => ({
      ...leg,
      branches: [...leg.branches, nextBranch],
      sampleBreakouts: leg.sampleBreakouts.map((breakout) => ({
        ...breakout,
        branchQuantities: {
          ...breakout.branchQuantities,
          [nextBranch.id]: "",
        },
      })),
    }));
    setActiveBranchIdsByLeg((current) => ({
      ...current,
      [legId]: nextBranch.id,
    }));
  };

  const handleDeleteBranch = (legId: string, branchId: string) => {
    const targetLeg = legs.find((leg) => leg.id === legId);
    if (!targetLeg) return;

    const nextBranches = targetLeg.branches
      .filter((branch) => branch.id !== branchId)
      .map((branch, index) => ({
        ...branch,
        name: String(index + 1),
      }));

    updateLeg(legId, (leg) => ({
      ...leg,
      branches: nextBranches,
      sampleBreakouts: leg.sampleBreakouts.map((breakout) => {
        const nextBranchQuantities = { ...breakout.branchQuantities };
        delete nextBranchQuantities[branchId];
        return {
          ...breakout,
          branchQuantities: nextBranchQuantities,
        };
      }),
    }));

    setActiveBranchIdsByLeg((current) => ({
      ...current,
      [legId]: nextBranches[0]?.id ?? SAMPLE_BREAKOUTS_TAB,
    }));
  };

  const handleAddSampleBreakout = (legId: string) => {
    const targetLeg = legs.find((leg) => leg.id === legId);
    if (!targetLeg) return;

    const nextBreakout = createSampleBreakout(targetLeg.sampleBreakouts.length);
    updateLeg(legId, (leg) => ({
      ...leg,
      sampleBreakouts: [...leg.sampleBreakouts, nextBreakout],
    }));
    setActiveBranchIdsByLeg((current) => ({
      ...current,
      [legId]: SAMPLE_BREAKOUTS_TAB,
    }));
  };

  const updateSampleBreakout = (
    legId: string,
    breakoutId: string,
    patch: Partial<SampleBreakout>
  ) => {
    updateLeg(legId, (leg) => ({
      ...leg,
      sampleBreakouts: leg.sampleBreakouts.map((breakout) =>
        breakout.id === breakoutId ? { ...breakout, ...patch } : breakout
      ),
    }));
  };

  const removeSampleBreakout = (legId: string, breakoutId: string) => {
    updateLeg(legId, (leg) => ({
      ...leg,
      sampleBreakouts: leg.sampleBreakouts.filter(
        (breakout) => breakout.id !== breakoutId
      ),
    }));
  };

  const insertRequirementRow = (legId: string, rowIndex: number) => {
    updateLeg(legId, (leg) => ({
      ...leg,
      branches: leg.branches.map((branch) => {
        const nextRequirements = normalizeRequirementRows(branch.requirements);
        nextRequirements.splice(rowIndex, 0, null);
        return { ...branch, requirements: nextRequirements };
      }),
    }));
  };

  const removeRequirementRow = (legId: string, rowIndex: number) => {
    updateLeg(legId, (leg) => {
      // A matrix row is shared by all branches. Removing one test collapses the
      // whole row so split branches cannot keep tests in now-invalid gaps.
      const nextBranches = leg.branches.map((branch) => ({
        ...branch,
        requirements: normalizeRequirementRows(branch.requirements).filter(
          (_, index) => index !== rowIndex
        ),
      }));

      return {
        ...leg,
        branches: trimTrailingEmptyRequirementRows(nextBranches),
      };
    });
  };

  const startDrag = (payload: DragPayload) => {
    setDragging(payload);
  };

  const clearDragState = () => {
    stopBranchMatrixAutoScroll();
    stopViewportAutoScroll();
    setDragging(null);
    setDragOverLegId(null);
    setDragOverRequirementId(null);
  };

  const canDropRequirementInSlot = (
    leg: Leg,
    branchId: string,
    rowIndex: number
  ) => {
    const targetBranchIndex = leg.branches.findIndex(
      (branch) => branch.id === branchId
    );
    if (targetBranchIndex < 0) return false;

    // Current branch-matrix rule from product: split branches can receive
    // tests freely except for their first row, which is reserved for main.
    return !(targetBranchIndex > 0 && rowIndex === 0);
  };

  const getBlockedSlotReason = (
    leg: Leg,
    branchId: string,
    rowIndex: number
  ) => {
    const targetBranchIndex = leg.branches.findIndex(
      (branch) => branch.id === branchId
    );
    if (targetBranchIndex < 0) return "";

    if (targetBranchIndex > 0 && rowIndex === 0) {
      return t("testFlow.blockedFirstSplitRow");
    }

    return "";
  };

  const getNextAvailableSlotIndex = (leg: Leg, branchId: string) => {
    const targetBranch = leg.branches.find((branch) => branch.id === branchId);
    if (!targetBranch) return 0;

    const rowCount = Math.max(
      targetBranch.requirements.length + 1,
      ...leg.branches.map((branch) => branch.requirements.length + 1)
    );

    for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
      if (
        !targetBranch.requirements[rowIndex] &&
        canDropRequirementInSlot(leg, branchId, rowIndex)
      ) {
        return rowIndex;
      }
    }

    return targetBranch.requirements.length;
  };

  const moveRequirementToBranch = (
    requirementId: string,
    targetLegId: string,
    targetBranchId: string,
    targetIndex?: number
  ) => {
    const poolRequirement = allRequirements.find(
      (requirement) => requirement.nodeid === requirementId
    );
    // A card moved within the flow keeps its instanceId (and its settings);
    // a requirement dropped from the pool becomes a new card.
    const movedRequirement =
      (dragging?.source === "leg"
        ? legs
            .find((leg) => leg.id === dragging.legId)
            ?.branches.find((branch) => branch.id === dragging.branchId)
            ?.requirements[dragging.index]
        : null) ??
      (poolRequirement ? createRequirementInstance(poolRequirement) : null);

    if (!movedRequirement || !dragging) return;

    setLegs((current) => {
      // Dragging from the left pool copies the requirement into the target leg
      // without removing it from any other leg. The same requirement may be
      // dropped repeatedly (e.g. a check before and after a test).
      if (dragging.source === "pool") {
        return current.map((leg) => {
          if (leg.id !== targetLegId) return leg;

          return {
            ...leg,
            branches: leg.branches.map((branch) => {
              if (branch.id !== targetBranchId) return branch;

              const nextRequirements = [...branch.requirements];
              const insertionIndex =
                typeof targetIndex === "number"
                  ? Math.max(0, targetIndex)
                  : nextRequirements.length;

              // Matrix placement must preserve the resolved row index, even if
              // that leaves empty cells before it. Clamping to array length
              // would move a valid row-2 variant drop back into row 1.
              nextRequirements[insertionIndex] = movedRequirement;
              return { ...branch, requirements: nextRequirements };
            }),
          };
        });
      }

      const sourceLegId = dragging.legId;
      const sourceBranchId = dragging.branchId;
      const sourceIndex = dragging.index;

      return current.map((leg) => {
        if (leg.id !== sourceLegId && leg.id !== targetLegId) return leg;

        return {
          ...leg,
          branches: leg.branches.map((branch) => {
            const isSource =
              leg.id === sourceLegId && branch.id === sourceBranchId;
            const isTarget =
              leg.id === targetLegId && branch.id === targetBranchId;

            if (!isSource && !isTarget) return branch;

            if (isSource && isTarget) {
              const nextRequirements = [...branch.requirements];
              const [draggedRequirement] = nextRequirements.splice(sourceIndex, 1);
              if (!draggedRequirement) return branch;

              // Same-column sorting should move the dragged card to the exact
              // row it was dropped on. We intentionally do not offset downward
              // moves here, otherwise dragging row 1 onto row 2 becomes a no-op.
              const adjustedIndex =
                typeof targetIndex === "number"
                  ? Math.max(
                      0,
                      Math.min(targetIndex, nextRequirements.length)
                    )
                  : nextRequirements.length;

              nextRequirements.splice(adjustedIndex, 0, draggedRequirement);
              return { ...branch, requirements: nextRequirements };
            }

            if (isSource) {
              const nextRequirements = [...branch.requirements];
              nextRequirements[sourceIndex] = null;
              return {
                ...branch,
                requirements: nextRequirements,
              };
            }

            const nextRequirements = [...branch.requirements];
            const insertionIndex =
              typeof targetIndex === "number"
                ? Math.max(0, targetIndex)
                : nextRequirements.length;

            // Cross-branch moves are matrix placements, not list insertions.
            // Keep the target row stable so one row cannot accidentally get
            // two tests across different branches.
            nextRequirements[insertionIndex] = movedRequirement;
            return { ...branch, requirements: nextRequirements };
          }),
        };
      });
    });
  };

  const handleDropOnBranch = (
    legId: string,
    branchId: string,
    targetIndex?: number
  ) => {
    if (!dragging) return;
    const targetLeg = legs.find((leg) => leg.id === legId);
    if (!targetLeg) return;

    const resolvedTargetIndex =
      typeof targetIndex === "number"
        ? targetIndex
        : getNextAvailableSlotIndex(targetLeg, branchId);

    if (!canDropRequirementInSlot(targetLeg, branchId, resolvedTargetIndex)) {
      toast.error(t("testFlow.invalidBranchDrop"));
      clearDragState();
      return;
    }

    moveRequirementToBranch(
      dragging.requirementId,
      legId,
      branchId,
      resolvedTargetIndex
    );
    setActiveLegId(legId);
    setActiveBranchIdsByLeg((current) => ({
      ...current,
      [legId]: branchId,
    }));
    clearDragState();
  };

  const handleDropOnBranchFallback = (legId: string, branchId: string) => {
    // Branch-level fallback is only for new requirements from the left list.
    // Assigned requirements must land on an explicit card/slot; otherwise a
    // cancelled drag would unexpectedly move the item to the end of the branch.
    if (dragging?.source !== "pool") {
      clearDragState();
      return;
    }

    handleDropOnBranch(legId, branchId);
  };

  // Edits are scoped to one card, so repeated tests keep separate settings.
  const openRequirementEditor = (instanceId: string) => {
    const fullRequirement =
      legs
        .flatMap((leg) => leg.branches)
        .flatMap((branch) => branch.requirements)
        .find((requirement) => requirement?.instanceId === instanceId) ||
      null;

    setEditingRequirement(fullRequirement);
    setIsRequirementEditorOpen(Boolean(fullRequirement));
  };

  const closeRequirementEditor = () => {
    setIsRequirementEditorOpen(false);
  };

  const handleRequirementSavedFromFlow = async (payload: any) => {
    if (!editingRequirement) return;

    const updatedLegs = legs.map((leg) => ({
      ...leg,
      branches: leg.branches.map((branch) => ({
        ...branch,
        requirements: branch.requirements.map((requirement) => {
          if (!requirement) return requirement;

          if (requirement.instanceId !== editingRequirement.instanceId) {
            return requirement;
          }

          const nextTestRequirementData = {
            ...buildTestRequirementData(requirement),
            ...payload,
            nodeid: requirement.nodeid,
            name:
              payload.name ??
              getRequirementName(editingRequirement) ??
              getRequirementName(requirement),
            category:
              payload.subcategory ??
              payload.category ??
              getRequirementCategory(editingRequirement) ??
              getRequirementCategory(requirement),
          };

          return {
            ...requirement,
            name: nextTestRequirementData.name,
            category: nextTestRequirementData.category,
            testrequirementdata: nextTestRequirementData,
          };
        }),
      })),
    }));

    const updatedLegsPayload = buildLegsPayload(updatedLegs);
    await updateTestPlanAPI({
      nodeid: testPlanNodeId,
      legs: updatedLegsPayload,
    });
    setLegs(updatedLegs);
    setSavedLegsSnapshot(JSON.stringify(updatedLegsPayload));
    await getTestPlanContent(testPlanNodeId);
    closeRequirementEditor();
  };

  const handleSave = async () => {
    if (isSaving) return;

    const currentLegsPayload = buildLegsPayload(legs);
    const currentSnapshot = JSON.stringify(currentLegsPayload);

    if (currentSnapshot === savedLegsSnapshot) {
      toast.info(t("testFlow.saveNoChanges"));
      return;
    }

    const invalidSampleBreakoutLeg = legs.find((leg) =>
      leg.sampleBreakouts.some((breakout) => {
        const quantity = Number(breakout.quantity);
        const hasInvalidBranchQuantity = leg.branches
          .slice(1)
          .some((branch) => {
            const branchQuantity = breakout.branchQuantities[branch.id] ?? "";
            const parsedBranchQuantity = Number(branchQuantity);
            return (
              branchQuantity === "" ||
              !Number.isInteger(parsedBranchQuantity) ||
              parsedBranchQuantity < 0
            );
          });

        return (
          breakout.name.trim().length === 0 ||
          breakout.name.length > 255 ||
          !Number.isInteger(quantity) ||
          quantity <= 0 ||
          hasInvalidBranchQuantity
        );
      })
    );

    if (invalidSampleBreakoutLeg) {
      toast.error(
        t("testFlow.invalidSampleBreakoutError", {
          legName: invalidSampleBreakoutLeg.name,
        })
      );
      return;
    }

    const overAllocatedSampleBreakoutLeg = legs.find((leg) =>
      leg.sampleBreakouts.some((breakout) => {
        const masterQuantity = Number(breakout.quantity || 0);
        const allocatedToVariantBranches = leg.branches
          .slice(1)
          .reduce(
            (sum, branch) =>
              sum + Number(breakout.branchQuantities[branch.id] || 0),
            0
          );

        return masterQuantity <= allocatedToVariantBranches;
      })
    );

    if (overAllocatedSampleBreakoutLeg) {
      toast.error(
        t("testFlow.sampleBreakoutAllocationError", {
          legName: overAllocatedSampleBreakoutLeg.name,
        })
      );
      return;
    }

    const noRemainingMainBranchLeg = legs.find((leg) => {
      if (leg.branches.length <= 1 || leg.sampleBreakouts.length === 0) {
        return false;
      }

      return !leg.sampleBreakouts.some((breakout) => {
        const masterQuantity = Number(breakout.quantity || 0);
        const allocatedToVariantBranches = leg.branches
          .slice(1)
          .reduce(
            (sum, branch) =>
              sum + Number(breakout.branchQuantities[branch.id] || 0),
            0
          );

        return masterQuantity - allocatedToVariantBranches > 0;
      });
    });

    if (noRemainingMainBranchLeg) {
      toast.error(
        t("testFlow.sampleBreakoutRemainingError", {
          legName: noRemainingMainBranchLeg.name,
        })
      );
      return;
    }

    const finalJson = {
      nodeid: testPlanNodeId,
      legs: currentLegsPayload,
    };

    console.log("Test Flow final JSON:", finalJson);
    setIsSaving(true);
    try {
      await updateTestPlanAPI(finalJson);
      await getTestPlanContent(testPlanNodeId);
      toast.success(t("testFlow.saveSuccess"));
    } catch (error) {
      console.error("Failed to save test flow", error);
      toast.error("Failed to save test flow");
    } finally {
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(buildLegsPayload(legs)) !== savedLegsSnapshot;
  }, [legs, savedLegsSnapshot]);

  const renderInsertRowButton = (legId: string, insertAtIndex: number) => (
    <Tooltip title={t("testFlow.insertRowBelow")}>
      <IconButton
        size="small"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          insertRequirementRow(legId, insertAtIndex);
        }}
        sx={{
          width: 32,
          height: 32,
          color: "primary.main",
          border: "1px solid",
          borderColor: "primary.light",
          bgcolor: "background.paper",
          "&:hover": {
            bgcolor: "rgba(0, 100, 153, 0.08)",
          },
        }}
      >
        <Add fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const renderRemoveEmptyRowButton = (legId: string, rowIndex: number) => (
    <Tooltip title={t("testFlow.removeEmptyRow")}>
      <IconButton
        size="small"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          removeRequirementRow(legId, rowIndex);
        }}
        sx={{
          width: 32,
          height: 32,
          color: "error.main",
          border: "1px solid",
          borderColor: "error.light",
          bgcolor: "background.paper",
          "&:hover": {
            bgcolor: "rgba(255, 61, 0, 0.08)",
          },
        }}
      >
        <RemoveCircleOutline fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
      >
        <Box>
          <Typography variant="h5">{t("testFlow.title")}</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.75}>
            {t("testFlow.subtitle")}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddLeg}>
            {t("testFlow.addLeg")}
          </Button>
        </Stack>
      </Box>

      <Divider sx={{ my: 2.5 }} />

      <Box
        display="flex"
        gap={2}
        flexDirection={{ xs: "column", lg: "row" }}
        alignItems="stretch"
        minWidth={0}
      >
        <Paper
          variant="outlined"
          sx={{
            width: { xs: "100%", lg: 360 },
            minHeight: 540,
            p: 2,
            borderRadius: 3,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            {t("testFlow.requirementsTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("testFlow.requirementsSubtitle")}
          </Typography>

          <TextField
            size="small"
            fullWidth
            value={requirementSearch}
            onChange={(event) => setRequirementSearch(event.target.value)}
            placeholder={t("testFlow.searchPlaceholder")}
            sx={{ mb: 1.5, flexShrink: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: requirementSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t("testFlow.clearSearch")}
                    size="small"
                    onClick={() => setRequirementSearch("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Stack spacing={2} minHeight={0} flex={1}>
            {requirements.length === 0 && (
              <Alert severity="info">{t("testFlow.noRequirements")}</Alert>
            )}
            {requirements.length > 0 && filteredRequirements.length === 0 && (
              <Alert severity="info">{t("testFlow.noSearchResults")}</Alert>
            )}

            <Stack
              spacing={1}
            sx={{
                minHeight: 0,
                maxHeight: { xs: "100vh !important", lg: "calc(100vh - 430px)" },
                overflowY: "auto",
                pr: 0.75,
              }}
            >
              {filteredRequirements.map((requirement) => {
                const isAssigned = assignedRequirementIds.has(
                  requirement.nodeid
                );
                const assignedLegNames =
                  assignedLegNamesByRequirement.get(requirement.nodeid) ?? [];

                return (
                  <Paper
                    key={requirement.nodeid}
                    draggable
                    onDragStart={() =>
                      startDrag({
                        source: "pool",
                        requirementId: requirement.nodeid,
                      })
                    }
                    onDragEnd={clearDragState}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "grab",
                      transition: "0.2s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: "0 10px 18px rgba(0, 100, 153, 0.10)",
                      },
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0, flex: 1 }}
                      >
                        <DragIndicator fontSize="small" color="disabled" />
                        <Box minWidth={0}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Tooltip title={getRequirementName(requirement)}>
                              <Typography
                                variant="body1"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {highlightSearchMatch(
                                  getRequirementName(requirement),
                                  requirementSearch
                                )}
                              </Typography>
                            </Tooltip>
                            {isAssigned ? (
                              <Tooltip
                                title={
                                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                    {assignedLegNames.map((legName) => (
                                      <Box
                                        component="li"
                                        key={legName}
                                        sx={{ mb: 0.25 }}
                                      >
                                        {legName}
                                      </Box>
                                    ))}
                                  </Box>
                                }
                                arrow
                              >
                                <InfoOutlined
                                  sx={{
                                    fontSize: 16,
                                    color: "info.main",
                                    cursor: "help",
                                  }}
                                />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Box>
                      </Stack>
                      {!isAssigned ? (
                        <Chip size="small" label={t("testFlow.unassigned")} />
                      ) : null}
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 540,
            p: 2,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
            minWidth={0}
          >
            <Tabs
              value={safeActiveLegId}
              onChange={(_, value) => {
                setActiveLegId(value);
                setEditingLegId(null);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                width: "100%",
                minWidth: 0,
                minHeight: 44,
                "& .MuiTabs-scroller": {
                  overflowX: "auto !important",
                },
                "& .MuiTabs-flexContainer": {
                  minWidth: "max-content",
                },
                "& .MuiTab-root": {
                  alignItems: "flex-start",
                  minHeight: 44,
                  minWidth: 140,
                  maxWidth: 220,
                  textTransform: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  borderBottom: 0,
                  borderTopLeftRadius: 10,
                  borderTopRightRadius: 10,
                  mr: 1,
                  bgcolor: "background.paper",
                },
                "& .Mui-selected": {
                  color: "primary.main",
                  fontWeight: 600,
                },
              }}
            >
              {legs.map((leg) => (
                <Tab
                  key={leg.id}
                  value={leg.id}
                  disableRipple
                  disableFocusRipple
                  onDoubleClick={() => setEditingLegId(leg.id)}
                  label={
                    editingLegId === leg.id ? (
                      <TextField
                        size="small"
                        value={leg.name}
                        onChange={(e) =>
                          updateLeg(leg.id, (current) => ({
                            ...current,
                            name: e.target.value || t("testFlow.untitledLeg"),
                          }))
                        }
                        onBlur={() => setEditingLegId(null)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") setEditingLegId(null);
                        }}
                        autoFocus
                        variant="standard"
                        sx={{ minWidth: 110 }}
                      />
                    ) : (
                      <Box
                        onDragOver={(event) => {
                          event.preventDefault();
                          setDragOverLegId(leg.id);
                        }}
                        onDragLeave={() => setDragOverLegId(null)}
                        onDrop={(event) => {
                          event.preventDefault();
                          const targetBranchId =
                            activeBranchIdsByLeg[leg.id] || leg.branches[0]?.id;
                          if (targetBranchId) {
                            handleDropOnBranchFallback(leg.id, targetBranchId);
                          }
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor:
                            dragOverLegId === leg.id
                              ? "rgba(0,100,153,0.08)"
                              : "transparent",
                        }}
                      >
                        <Tooltip title={leg.name}>
                          <Box
                            component="span"
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              display: "inline-block",
                            }}
                          >
                            {leg.name}
                          </Box>
                        </Tooltip>
                        <Box
                          component="span"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            setEditingLegId(leg.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              setEditingLegId(leg.id);
                            }
                          }}
                          sx={{
                            p: 0.25,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            borderRadius: 1,
                            "&:focus-visible": {
                              outline: "2px solid",
                              outlineColor: "primary.main",
                              outlineOffset: 1,
                            },
                          }}
                        >
                          <EditOutlined sx={{ fontSize: 16, opacity: 0.6 }} />
                        </Box>
                      </Box>
                    )
                  }
                />
              ))}
            </Tabs>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {activeLeg ? (
            <Box
              onDragOver={(event) => {
                event.preventDefault();
                setDragOverLegId(activeLeg.id);
              }}
              onDragLeave={() => {
                setDragOverLegId((current) =>
                  current === activeLeg.id ? null : current
                );
                setDragOverRequirementId(null);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (activeBranch) {
                  handleDropOnBranchFallback(activeLeg.id, activeBranch.id);
                }
              }}
              sx={{
                flex: 1,
                border: "1px dashed",
                borderColor:
                  dragOverLegId === activeLeg.id ? "primary.main" : "divider",
                borderRadius: 3,
                p: 2,
                bgcolor:
                  dragOverLegId === activeLeg.id
                    ? "rgba(0,100,153,0.03)"
                    : "transparent",
              }}
            >
              <Stack spacing={1.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                >
                  <Box flex={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {t("testFlow.assignedTitle", { legName: activeLeg.name })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("testFlow.assignedSubtitle")}
                    </Typography>
                  </Box>

                  <Box
                    width={{ xs: "100%", md: 240 }}
                    display="flex"
                    flexDirection="column"
                    gap={1.25}
                    alignItems={{ xs: "flex-start", md: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteOutline />}
                      onClick={handleDeleteLeg}
                      sx={{ px: 1.5 }}
                    >
                      {t("testFlow.delete")}
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={1.5}
                    sx={{ mt: 1 }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t("testFlow.sampleBreakoutsTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("testFlow.sampleBreakoutsSubtitle")}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleAddSampleBreakout(activeLeg.id)}
                      sx={{ flexShrink: 0 }}
                    >
                      {t("testFlow.addSampleBreakout")}
                    </Button>
                  </Box>

                  {activeLeg.sampleBreakouts.length === 0 ? (
                    <Alert severity="info" sx={{ mt: 1.5 }}>
                      {t("testFlow.noSampleBreakouts")}
                    </Alert>
                  ) : (
                    <Stack spacing={1.25} sx={{ mt: 1.5 }}>
                      {activeLeg.sampleBreakouts.map((breakout, index) => {
                        const masterQuantity = Number(breakout.quantity);
                        const hasNameError =
                          breakout.name.trim().length === 0 ||
                          breakout.name.length > 255;
                        const hasMasterQuantityError =
                          breakout.quantity !== "" &&
                          (!Number.isInteger(masterQuantity) ||
                            masterQuantity <= 0);

                        return (
                          <Paper
                            key={breakout.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                  xs: "1fr",
                                  md: `minmax(220px, 1fr) repeat(${Math.max(
                                    activeLeg.branches.length,
                                    1
                                  )}, minmax(140px, 160px)) auto`,
                                },
                                gap: 1.25,
                                alignItems: "flex-start",
                                minWidth: 0,
                                pt: 1,
                                overflowX: "auto",
                              }}
                            >
                              <TextField
                                fullWidth
                                required
                                size="small"
                                label={t("testFlow.sampleBreakoutName")}
                                value={breakout.name}
                                onChange={(event) =>
                                  updateSampleBreakout(activeLeg.id, breakout.id, {
                                    name: event.target.value.slice(0, 255),
                                  })
                                }
                                error={hasNameError}
                                helperText={
                                  hasNameError
                                    ? t("testFlow.sampleBreakoutNameHelper")
                                    : `${breakout.name.length}/255`
                                }
                              />
                              {activeLeg.branches.map((branch, branchIndex) => {
                                const branchQuantity =
                                  branchIndex === 0
                                    ? breakout.quantity
                                    : breakout.branchQuantities[branch.id] ?? "";
                                const parsedBranchQuantity =
                                  Number(branchQuantity);
                                const hasBranchQuantityError =
                                  branchQuantity !== "" &&
                                  (!Number.isInteger(parsedBranchQuantity) ||
                                    parsedBranchQuantity < 0);

                                return (
                                  <TextField
                                    key={branch.id}
                                    required={branchIndex === 0}
                                    type="number"
                                    size="small"
                                    label={`${activeLeg.name} ${branch.name}`}
                                    value={branchQuantity}
                                    onChange={(event) => {
                                      if (branchIndex === 0) {
                                        updateSampleBreakout(
                                          activeLeg.id,
                                          breakout.id,
                                          {
                                            quantity: event.target.value,
                                          }
                                        );
                                        return;
                                      }

                                      updateSampleBreakout(
                                        activeLeg.id,
                                        breakout.id,
                                        {
                                          branchQuantities: {
                                            ...breakout.branchQuantities,
                                            [branch.id]: event.target.value,
                                          },
                                        }
                                      );
                                    }}
                                    error={
                                      branchIndex === 0
                                        ? hasMasterQuantityError
                                        : hasBranchQuantityError
                                    }
                                    helperText={
                                      branchIndex === 0
                                        ? t("testFlow.mainBranch")
                                        : t("testFlow.variantBranch")
                                    }
                                    inputProps={{
                                      min: branchIndex === 0 ? 1 : 0,
                                      step: 1,
                                    }}
                                  />
                                );
                              })}
                              <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="flex-end"
                                gap={1}
                                sx={{ pt: { xs: 0, md: 0.5 } }}
                              >
                                <Chip
                                  size="small"
                                  label={t("testFlow.orderLabel", {
                                    index: index + 1,
                                  })}
                                  variant="outlined"
                                />
                              <IconButton
                                size="small"
                                onClick={() =>
                                  removeSampleBreakout(activeLeg.id, breakout.id)
                                }
                                sx={{ color: "error.main" }}
                              >
                                <RemoveCircleOutline fontSize="small" />
                              </IconButton>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Stack>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", md: "center" }}
                    flexDirection={{ xs: "column", md: "row" }}
                    gap={1.5}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {t("testFlow.branchMatrixTitle")}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("testFlow.branchMatrixSubtitle")}
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.5}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleAddBranch(activeLeg.id)}
                      sx={{ flexShrink: 0 }}
                    >
                      {t("testFlow.addBranch")}
                    </Button>
                    {activeBranch ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<DeleteOutline />}
                        onClick={() =>
                          handleDeleteBranch(activeLeg.id, activeBranch.id)
                        }
                        sx={{ flexShrink: 0 }}
                      >
                        {t("testFlow.deleteBranch")}
                      </Button>
                    ) : null}
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />
                </Box>

                {/* Branches are rendered as a side-by-side matrix so users can
                    place tests into explicit row slots. The only blocked slot
                    is row 1 of split branches, which stays reserved for main. */}
                <Box
                  ref={branchMatrixScrollRef}
                  onDragOver={handleBranchMatrixAutoScroll}
                  onDragLeave={stopBranchMatrixAutoScroll}
                  onDrop={stopBranchMatrixAutoScroll}
                  sx={{
                    display: "grid",
                    // Keep branches side-by-side at every viewport size so
                    // tests can be dragged across split branches without moving down
                    // a long stacked column layout.
                    gridTemplateColumns: `repeat(${Math.max(
                      activeLeg.branches.length,
                      1
                    )}, minmax(280px, 1fr))`,
                    gap: 1.5,
                    overflowX: "auto",
                    scrollBehavior: "auto",
                    pb: 1,
                    "&::-webkit-scrollbar": {
                      height: 10,
                    },
                    "&::-webkit-scrollbar-track": {
                      bgcolor: "rgba(0, 0, 0, 0.05)",
                      borderRadius: 999,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      bgcolor: "rgba(0, 0, 0, 0.24)",
                      borderRadius: 999,
                    },
                  }}
                >
                  {activeLeg.branches.map((branch, branchIndex) => {
                    const populatedRowCount = Math.max(
                      1,
                      ...activeLeg.branches.map(
                        (currentBranch) => currentBranch.requirements.length
                      )
                    );
                    const hasAssignedRequirement = activeLeg.branches.some(
                      (currentBranch) =>
                        currentBranch.requirements.some(Boolean)
                    );
                    const rowCount = hasAssignedRequirement
                      ? populatedRowCount + 1
                      : populatedRowCount;
                    const isSelectedBranch = activeBranchId === branch.id;

                    return (
                      <Paper
                        key={branch.id}
                        variant="outlined"
                        onClick={() =>
                          setActiveBranchIdsByLeg((current) => ({
                            ...current,
                            [activeLeg.id]: branch.id,
                          }))
                        }
                        sx={{
                          minWidth: 280,
                          borderRadius: 2,
                          overflow: "hidden",
                          borderColor: isSelectedBranch
                            ? "primary.main"
                            : "divider",
                          bgcolor: isSelectedBranch
                            ? "rgba(0, 100, 153, 0.03)"
                            : "background.paper",
                        }}
                      >
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1.25,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            bgcolor: "grey.50",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: branchIndex === 0 ? "primary.main" : "text.primary",
                            }}
                          >
                            {activeLeg.name} {branch.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {branchIndex === 0
                              ? t("testFlow.mainBranch")
                              : t("testFlow.variantBranch")}
                          </Typography>
                        </Box>

                        <Stack spacing={1} sx={{ p: 1.25 }}>
                          {Array.from({ length: rowCount }).map((_, rowIndex) => {
                            const requirement = branch.requirements[rowIndex];
                            const dropSlotId = `${branch.id}-${rowIndex}`;
                            const blockedReason = getBlockedSlotReason(
                              activeLeg,
                              branch.id,
                              rowIndex
                            );
                            const isSlotBlocked = Boolean(blockedReason);
                            const isSlotActive =
                              dragOverRequirementId === dropSlotId;
                            const canInsertRowAfter =
                              branchIndex === 0 &&
                              hasAssignedRequirement &&
                              rowIndex < populatedRowCount - 1;
                            const canRemoveEmptyRow =
                              hasAssignedRequirement &&
                              rowIndex < populatedRowCount &&
                              activeLeg.branches.every(
                                (currentBranch) =>
                                  !currentBranch.requirements[rowIndex]
                              );

                            if (!requirement) {
                              return (
                                <Tooltip
                                  key={dropSlotId}
                                  title={blockedReason}
                                  arrow
                                  disableHoverListener={!isSlotBlocked}
                                >
                                  <Box
                                    onDragOver={(event) => {
                                      if (isSlotBlocked) return;
                                      event.preventDefault();
                                      setDragOverRequirementId(dropSlotId);
                                    }}
                                    onDragLeave={() => {
                                      if (isSlotBlocked) return;
                                      setDragOverRequirementId((current) =>
                                        current === dropSlotId ? null : current
                                      );
                                    }}
                                    onDrop={(event) => {
                                      if (isSlotBlocked) return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      handleDropOnBranch(
                                        activeLeg.id,
                                        branch.id,
                                        rowIndex
                                      );
                                    }}
                                    sx={{
                                      minHeight: 86,
                                      border: "1px dashed",
                                      borderColor: isSlotBlocked
                                        ? "warning.light"
                                        : isSlotActive
                                          ? "primary.main"
                                          : "primary.light",
                                      borderRadius: 2,
                                      bgcolor: isSlotBlocked
                                        ? "rgba(255, 152, 0, 0.06)"
                                        : isSlotActive
                                          ? "rgba(0,100,153,0.10)"
                                          : "rgba(0,100,153,0.035)",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      px: 1.5,
                                      py: 1,
                                      color: isSlotBlocked
                                        ? "warning.dark"
                                        : "primary.main",
                                      cursor: isSlotBlocked
                                        ? "not-allowed"
                                        : "copy",
                                      transition:
                                        "background-color 120ms ease, border-color 120ms ease",
                                    }}
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      alignItems="center"
                                      justifyContent="center"
                                      sx={{
                                        minWidth: 0,
                                        textAlign: "center",
                                      }}
                                    >
                                      {isSlotBlocked ? (
                                        <LockOutlined sx={{ fontSize: 16 }} />
                                      ) : (
                                        <PlaylistAdd sx={{ fontSize: 17 }} />
                                      )}
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: isSlotActive ? 700 : 600,
                                          lineHeight: 1.35,
                                        }}
                                      >
                                        {isSlotBlocked
                                          ? blockedReason
                                          : t("testFlow.availableDropSlot")}
                                      </Typography>
                                    </Stack>
                                    {canInsertRowAfter || canRemoveEmptyRow ? (
                                      <Stack
                                        direction="row"
                                        spacing={0.75}
                                        justifyContent="flex-end"
                                        sx={{
                                          width: "100%",
                                          mt: 1,
                                          pt: 0.75,
                                          borderTop: "1px solid",
                                          borderColor: isSlotBlocked
                                            ? "rgba(255, 152, 0, 0.18)"
                                            : "rgba(0, 100, 153, 0.16)",
                                        }}
                                      >
                                        {canInsertRowAfter
                                          ? renderInsertRowButton(
                                              activeLeg.id,
                                              rowIndex + 1
                                            )
                                          : null}
                                        {canRemoveEmptyRow
                                          ? renderRemoveEmptyRowButton(
                                              activeLeg.id,
                                              rowIndex
                                            )
                                          : null}
                                      </Stack>
                                    ) : null}
                                  </Box>
                                </Tooltip>
                              );
                            }

                            // Cards are identified by instanceId: the same requirement
                            // can appear several times in a branch.
                            const cardId =
                              requirement.instanceId ?? requirement.nodeid;

                            return (
                              <Paper
                                key={cardId}
                                draggable
                                onDragStart={() =>
                                  startDrag({
                                    source: "leg",
                                    requirementId: requirement.nodeid,
                                    legId: activeLeg.id,
                                    branchId: branch.id,
                                    index: rowIndex,
                                  })
                                }
                                onDragEnd={clearDragState}
                                onDragOver={(event) => {
                                  event.preventDefault();
                                  setDragOverRequirementId(cardId);
                                }}
                                onDragLeave={() => {
                                  setDragOverRequirementId((current) =>
                                    current === cardId ? null : current
                                  );
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  handleDropOnBranch(
                                    activeLeg.id,
                                    branch.id,
                                    rowIndex
                                  );
                                }}
                                variant="outlined"
                                sx={{
                                  minHeight: 86,
                                  p: 1.25,
                                  borderRadius: 2,
                                  position: "relative",
                                  cursor: "grab",
                                  borderColor:
                                    dragOverRequirementId === cardId
                                      ? "primary.main"
                                      : "divider",
                                  boxShadow:
                                    dragOverRequirementId === cardId
                                      ? "0 8px 18px rgba(0, 100, 153, 0.10)"
                                      : "none",
                                }}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="space-between"
                                  gap={1}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ minWidth: 0, flex: 1 }}
                                  >
                                    <DragIndicator
                                      fontSize="small"
                                      color="disabled"
                                    />
                                    <Box minWidth={0}>
                                      <Tooltip title={getRequirementName(requirement)}>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {getRequirementName(requirement)}
                                        </Typography>
                                      </Tooltip>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {getRequirementCategory(requirement)}
                                      </Typography>
                                    </Box>
                                  </Stack>

                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    gap={0.75}
                                    sx={{ ml: "auto", flexShrink: 0 }}
                                  >
                                    <Chip
                                      size="small"
                                      label={t("testFlow.orderLabel", {
                                        index: rowIndex + 1,
                                      })}
                                      color="primary"
                                      variant="outlined"
                                    />
                                    {canInsertRowAfter
                                      ? renderInsertRowButton(
                                          activeLeg.id,
                                          rowIndex + 1
                                        )
                                      : null}
                                    <IconButton
                                      size="small"
                                      onMouseDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openRequirementEditor(cardId);
                                      }}
                                      sx={{ color: "primary.main" }}
                                    >
                                      <EditOutlined fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onMouseDown={(event) =>
                                        event.stopPropagation()
                                      }
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        removeRequirementRow(
                                          activeLeg.id,
                                          rowIndex
                                        );
                                      }}
                                      sx={{ color: "error.main" }}
                                    >
                                      <RemoveCircleOutline fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Box>

              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 3,
                p: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Alert severity="info" sx={{ width: "100%", maxWidth: 420 }}>
                {t("testFlow.noLegs")}
              </Alert>
            </Box>
          )}

          <Box
            mt={2}
            display="flex"
            justifyContent="flex-end"
            alignItems={{ xs: "stretch", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={2}
          >
            <Stack direction="row" spacing={1.5} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!hasUnsavedChanges || isSaving}
                startIcon={
                  isSaving ? <CircularProgress size={18} color="inherit" /> : null
                }
              >
                {t("testFlow.save")}
              </Button>
            </Stack>
          </Box>
        </Paper>
      </Box>
    </Paper>

    <Dialog
      open={isRequirementEditorOpen}
      onClose={closeRequirementEditor}
      TransitionProps={{
        onExited: () => setEditingRequirement(null),
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        Edit Test Requirement
        <IconButton
          size="small"
          onClick={closeRequirementEditor}
          aria-label="Close"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {editingRequirement ? (
          <TestRequirementForm
            requirement={editingRequirement as any}
            selectedId={editingRequirement.nodeid}
            resetTree={() => getTestPlanContent(testPlanNodeId)}
            setSelectedId={(id: string | null) => {
              if (id === null) {
                closeRequirementEditor();
              }
            }}
            entityType="testplan"
            parentNodeId={testPlanNodeId}
            onSaved={handleRequirementSavedFromFlow}
            saveScope="leg"
          />
        ) : null}
      </DialogContent>
    </Dialog>
    </>
  );
}
